import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { SiteHeader } from '../components/SiteLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Plus, MagnifyingGlass, BookOpen, Tag, Pencil, Trash, ClockCounterClockwise,
  DownloadSimple, Upload, X, Flask, Warning, CheckCircle, Info,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  custom:    { label: 'Custom',    cls: 'bg-gray-100 text-gray-700 border-gray-300' },
  screened:  { label: 'Screened',  cls: 'bg-[#E1EFFE] text-[#1E429F] border-[#A4CAFE]' },
  assessed:  { label: 'Assessed',  cls: 'bg-[#DEF7EC] text-[#03543F] border-[#31C48D]' },
  imported:  { label: 'Imported',  cls: 'bg-[#FEF3C7] text-[#723B13] border-[#F3CC64]' },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.custom;
  return (
    <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${m.cls}`} data-testid={`status-${status}`}>
      {m.label}
    </span>
  );
}

function flagChips(flags = {}) {
  const out = [];
  if (flags.genotoxicity) out.push({ k: 'genotox', label: 'Genotoxic', severe: true });
  if (flags.mutagenicity) out.push({ k: 'muta', label: 'Mutagenic', severe: true });
  if (flags.eu_annex_ii) out.push({ k: 'eu2', label: 'EU Banned', severe: true });
  if (flags.eu_annex_iii) out.push({ k: 'eu3', label: 'EU Restricted', severe: false });
  if (flags.ifra_restricted) out.push({ k: 'ifra', label: 'IFRA', severe: false });
  if (flags.prop_65) out.push({ k: 'p65', label: 'Prop 65', severe: false });
  if (flags.skin_sensitization && !['None', 'Not classified', ''].includes(flags.skin_sensitization)) {
    out.push({ k: 'sens', label: `Sens ${flags.skin_sensitization}`, severe: flags.skin_sensitization === 'Strong' });
  }
  if (typeof flags.mos === 'number') {
    out.push({ k: 'mos', label: `MoS ${flags.mos}`, severe: flags.mos < 100 });
  }
  return out;
}

function IngredientRow({ row, onEdit, onDelete, onHistory }) {
  const chips = flagChips(row.flags);
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50" data-testid={`ingredient-row-${row.id}`}>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <Flask size={14} className="text-[#4B5563]" />
          <div>
            <p className="text-sm font-semibold text-[#111827]">{row.inci_name}</p>
            <p className="text-[11px] text-[#4B5563] font-mono tabular-nums">
              {row.cas_number || '—'}{row.function_class ? ` · ${row.function_class}` : ''}
            </p>
          </div>
        </div>
      </td>
      <td className="py-3 px-4"><StatusPill status={row.status} /></td>
      <td className="py-3 px-4">
        <div className="flex flex-wrap gap-1">
          {(row.tags || []).map(t => (
            <span key={t} className="text-[10px] font-semibold px-1.5 py-0.5 bg-[#F3F4F6] text-[#111827] border border-gray-200 rounded">
              {t}
            </span>
          ))}
          {(!row.tags || row.tags.length === 0) && <span className="text-[10px] text-[#9CA3AF]">—</span>}
        </div>
      </td>
      <td className="py-3 px-4">
        <div className="flex flex-wrap gap-1">
          {chips.length === 0 ? <span className="text-[10px] text-[#9CA3AF]">none</span> : chips.map(c => (
            <span key={c.k}
              className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${
                c.severe
                  ? 'bg-[#FDE8E8] border-[#F8B4B4] text-[#9B1C1C]'
                  : 'bg-[#FEF3C7] border-[#F3CC64] text-[#723B13]'
              }`}>
              {c.label}
            </span>
          ))}
        </div>
      </td>
      <td className="py-3 px-4 text-[11px] text-[#4B5563] tabular-nums font-mono">v{row.version}</td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-1 justify-end">
          <button onClick={() => onHistory(row)} className="p-1.5 text-[#4B5563] hover:text-[#111827] hover:bg-gray-100 rounded" title="Version history" data-testid={`history-btn-${row.id}`}>
            <ClockCounterClockwise size={16} />
          </button>
          <button onClick={() => onEdit(row)} className="p-1.5 text-[#4B5563] hover:text-[#111827] hover:bg-gray-100 rounded" title="Edit" data-testid={`edit-btn-${row.id}`}>
            <Pencil size={16} />
          </button>
          <button onClick={() => onDelete(row)} className="p-1.5 text-[#4B5563] hover:text-[#9B1C1C] hover:bg-red-50 rounded" title="Delete" data-testid={`delete-btn-${row.id}`}>
            <Trash size={16} />
          </button>
        </div>
      </td>
    </tr>
  );
}

function EditIngredientDialog({ open, onClose, initial, onSave }) {
  const [form, setForm] = useState({
    inci_name: '', cas_number: '', function_class: '',
    concentration_typical: '', notes: '', tags: [], status: 'custom',
  });
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        inci_name: initial?.inci_name || '',
        cas_number: initial?.cas_number || '',
        function_class: initial?.function_class || '',
        concentration_typical: initial?.concentration_typical ?? '',
        notes: initial?.notes || '',
        tags: initial?.tags || [],
        status: initial?.status || 'custom',
      });
      setTagInput('');
    }
  }, [open, initial]);

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.tags.includes(t)) setForm({ ...form, tags: [...form.tags, t] });
    setTagInput('');
  };
  const removeTag = (t) => setForm({ ...form, tags: form.tags.filter(x => x !== t) });

  const submit = async () => {
    setSaving(true);
    try {
      await onSave({
        ...form,
        concentration_typical: form.concentration_typical === '' ? null : parseFloat(form.concentration_typical),
      });
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl" data-testid="edit-ingredient-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? 'Edit Ingredient' : 'Add Ingredient'}</DialogTitle>
          <DialogDescription>Entries in your private library are scoped to your account only.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-[#4B5563] uppercase tracking-wider">INCI name *</label>
              <Input value={form.inci_name} onChange={e => setForm({ ...form, inci_name: e.target.value })} className="h-9 text-sm mt-1" data-testid="form-inci-input" />
            </div>
            <div>
              <label className="text-xs font-bold text-[#4B5563] uppercase tracking-wider">CAS number</label>
              <Input value={form.cas_number} onChange={e => setForm({ ...form, cas_number: e.target.value })} className="h-9 text-sm mt-1" data-testid="form-cas-input" />
            </div>
            <div>
              <label className="text-xs font-bold text-[#4B5563] uppercase tracking-wider">Function</label>
              <Input value={form.function_class} onChange={e => setForm({ ...form, function_class: e.target.value })} className="h-9 text-sm mt-1" data-testid="form-function-input" />
            </div>
            <div>
              <label className="text-xs font-bold text-[#4B5563] uppercase tracking-wider">Typical %</label>
              <Input type="number" step="0.01" value={form.concentration_typical} onChange={e => setForm({ ...form, concentration_typical: e.target.value })} className="h-9 text-sm mt-1" data-testid="form-conc-input" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-bold text-[#4B5563] uppercase tracking-wider">Status</label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger className="h-9 text-sm mt-1" data-testid="form-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom</SelectItem>
                  <SelectItem value="screened">Screened</SelectItem>
                  <SelectItem value="assessed">Assessed</SelectItem>
                  <SelectItem value="imported">Imported</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-[#4B5563] uppercase tracking-wider">Tags</label>
            <div className="flex gap-2 mt-1">
              <Input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())} placeholder="Add a tag" className="h-9 text-sm" data-testid="form-tag-input" />
              <Button onClick={addTag} variant="outline" className="h-9 rounded" data-testid="form-add-tag-btn"><Plus size={14} /></Button>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {form.tags.map(t => (
                <span key={t} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 bg-[#F3F4F6] border border-gray-200 rounded">
                  {t}
                  <button onClick={() => removeTag(t)} className="hover:text-[#9B1C1C]"><X size={10} /></button>
                </span>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-[#4B5563] uppercase tracking-wider">Notes</label>
            <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={4} className="text-sm mt-1" data-testid="form-notes-input" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded">Cancel</Button>
          <Button onClick={submit} disabled={!form.inci_name.trim() || saving} className="btn-primary" data-testid="form-save-btn">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ open, onClose, onImported }) {
  const [library, setLibrary] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set()); setSearch('');
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API_URL}/api/ingredients`, { params: { limit: 500 }, withCredentials: true });
        setLibrary(r.data.ingredients || []);
      } catch (e) {
        console.warn('[LibraryPage] import dialog load failed:', e?.response?.status || e?.message);
        setLibrary([]);
      } finally { setLoading(false); }
    })();
  }, [open]);

  const filtered = useMemo(() => library.filter(i =>
    !search || i.inci_name.toLowerCase().includes(search.toLowerCase()) || (i.cas_number || '').includes(search)
  ), [library, search]);

  const toggle = (name) => {
    const n = new Set(selected);
    n.has(name) ? n.delete(name) : n.add(name);
    setSelected(n);
  };

  const submit = async () => {
    if (selected.size === 0) return;
    setImporting(true);
    try {
      const r = await axios.post(`${API_URL}/api/ingredients/private/import-from-library`,
        { inci_names: Array.from(selected) }, { withCredentials: true });
      onImported?.(r.data);
      onClose();
    } catch (e) {
      console.warn('[LibraryPage] import submit failed:', e?.response?.status || e?.message);
    } finally { setImporting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle>Import from Aᴳ Library</DialogTitle>
          <DialogDescription>Copy ingredients from the shared Aᴳ intelligence library into your private DB. Existing entries are skipped automatically.</DialogDescription>
        </DialogHeader>

        <div className="relative mb-3">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search INCI or CAS…"
            className="h-9 text-sm pl-9" data-testid="import-search-input" />
        </div>

        <div className="flex-1 overflow-y-auto border border-gray-200 rounded">
          {loading ? (
            <div className="p-6 text-center text-sm text-[#4B5563]">Loading Aᴳ library…</div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-sm text-[#4B5563]">No matches.</div>
          ) : filtered.map(ing => (
            <label key={ing.inci_name} className="flex items-center gap-3 p-2.5 border-b border-gray-100 hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={selected.has(ing.inci_name)} onChange={() => toggle(ing.inci_name)}
                data-testid={`import-check-${ing.inci_name}`}
                className="accent-[#111827] w-4 h-4" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-[#111827]">{ing.inci_name}</p>
                <p className="text-[11px] text-[#4B5563] font-mono">{ing.cas_number || '—'}{ing.function_class ? ` · ${ing.function_class}` : ''}</p>
              </div>
              <div className="flex gap-1">
                {ing.eu_annex_ii && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-[#FDE8E8] text-[#9B1C1C] border border-[#F8B4B4] rounded">EU Banned</span>}
                {ing.genotoxicity && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-[#FDE8E8] text-[#9B1C1C] border border-[#F8B4B4] rounded">Genotox</span>}
                {ing.prop_65 && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-[#FEF3C7] text-[#723B13] border border-[#F3CC64] rounded">Prop 65</span>}
              </div>
            </label>
          ))}
        </div>

        <DialogFooter>
          <span className="text-xs text-[#4B5563] mr-auto self-center">{selected.size} selected</span>
          <Button variant="outline" onClick={onClose} className="rounded">Cancel</Button>
          <Button onClick={submit} disabled={selected.size === 0 || importing} className="btn-primary" data-testid="import-confirm-btn">
            {importing ? 'Importing…' : `Import ${selected.size || ''}`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ open, onClose, row }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl" data-testid="history-dialog">
        <DialogHeader>
          <DialogTitle>Version History — {row?.inci_name}</DialogTitle>
          <DialogDescription>Current version v{row?.version}. Last 20 changes are retained.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[50vh] overflow-y-auto space-y-2">
          {(!row?.history || row.history.length === 0) && (
            <p className="text-sm text-[#4B5563]">No prior versions yet.</p>
          )}
          {[...(row?.history || [])].reverse().map((h) => (
            <div key={h.version} className="border border-gray-200 rounded p-3 text-xs">
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono font-bold text-[#111827]">v{h.version}</span>
                <span className="text-[#4B5563]">{h.updated_at?.slice(0, 19).replace('T', ' ') || '—'}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-[#4B5563]">Status:</span> {h.status}</div>
                <div><span className="text-[#4B5563]">Tags:</span> {(h.tags || []).join(', ') || '—'}</div>
              </div>
              {h.notes && <p className="mt-1 text-[#4B5563]"><span className="font-semibold">Notes:</span> {h.notes}</p>}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LibraryPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRow, setHistoryRow] = useState(null);

  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.q = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (tagFilter !== 'all') params.tag = tagFilter;
      const r = await axios.get(`${API_URL}/api/ingredients/private`, { params, withCredentials: true });
      setItems(r.data.ingredients || []);
      setTotal(r.data.total || 0);
      setAllTags(r.data.all_tags || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, statusFilter, tagFilter]);

  useEffect(() => {
    const t = setTimeout(fetchList, 250);
    return () => clearTimeout(t);
  }, [fetchList]);

  const handleSave = async (form) => {
    if (editing?.id) {
      const r = await axios.put(`${API_URL}/api/ingredients/private/${editing.id}`, form, { withCredentials: true });
      showToast(`Updated ${r.data.inci_name} → v${r.data.version}`, 'success');
    } else {
      const r = await axios.post(`${API_URL}/api/ingredients/private`, form, { withCredentials: true });
      showToast(`Added ${r.data.inci_name}`, 'success');
    }
    fetchList();
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete "${row.inci_name}" from your private library? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/ingredients/private/${row.id}`, { withCredentials: true });
      showToast(`Deleted ${row.inci_name}`, 'success');
      fetchList();
    } catch (e) { showToast('Delete failed', 'error'); }
  };

  const stats = useMemo(() => ({
    total,
    screened: items.filter(i => i.status === 'screened').length,
    assessed: items.filter(i => i.status === 'assessed').length,
    flagged: items.filter(i => {
      const f = i.flags || {};
      return f.genotoxicity || f.mutagenicity || f.eu_annex_ii || (typeof f.mos === 'number' && f.mos < 100);
    }).length,
  }), [items, total]);

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SiteHeader />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-[#111827]">Private Ingredient Library</h1>
            <p className="text-sm text-[#4B5563] flex items-center gap-1">
              <Info size={13} /> Scoped to your account — not visible to any other client.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)} className="rounded h-9 text-sm" data-testid="open-import-btn">
              <Upload size={16} className="mr-1.5" /> Import from Aᴳ Library
            </Button>
            <Button onClick={() => { setEditing(null); setEditOpen(true); }} className="btn-primary h-9 text-sm" data-testid="open-add-btn">
              <Plus size={16} className="mr-1.5" /> Add Ingredient
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="card-flat p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-1">Total</p>
            <p className="text-2xl font-black tabular-nums text-[#111827]" data-testid="stat-total">{stats.total}</p>
          </div>
          <div className="card-flat p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-1">Screened</p>
            <p className="text-2xl font-black tabular-nums text-[#1E429F]">{stats.screened}</p>
          </div>
          <div className="card-flat p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-1">Assessed</p>
            <p className="text-2xl font-black tabular-nums text-[#03543F]">{stats.assessed}</p>
          </div>
          <div className="card-flat p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-1">Flagged</p>
            <p className="text-2xl font-black tabular-nums text-[#9B1C1C]">{stats.flagged}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="card-flat">
          <div className="p-4 border-b border-gray-200 flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search INCI or CAS…"
                data-testid="library-search-input"
                className="w-full h-9 pl-9 pr-3 rounded border border-gray-200 bg-white text-sm outline-none focus:ring-2 focus:ring-[#111827] focus:border-transparent" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 text-sm w-[160px] rounded" data-testid="filter-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
                <SelectItem value="screened">Screened</SelectItem>
                <SelectItem value="assessed">Assessed</SelectItem>
                <SelectItem value="imported">Imported</SelectItem>
              </SelectContent>
            </Select>
            <Select value={tagFilter} onValueChange={setTagFilter}>
              <SelectTrigger className="h-9 text-sm w-[160px] rounded" data-testid="filter-tag-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tags</SelectItem>
                {allTags.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-10 text-center text-sm text-[#4B5563]">Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-12 text-center">
                <BookOpen size={36} className="mx-auto text-gray-300 mb-3" />
                <p className="text-sm font-semibold text-[#111827]">Your private library is empty</p>
                <p className="text-xs text-[#4B5563] mt-1 mb-4">Add an ingredient manually, import from the Aᴳ library, or capture from a completed assessment.</p>
                <div className="flex gap-2 justify-center">
                  <Button variant="outline" onClick={() => setImportOpen(true)} className="rounded h-9 text-sm"><Upload size={14} className="mr-1.5" />Import</Button>
                  <Button onClick={() => { setEditing(null); setEditOpen(true); }} className="btn-primary h-9 text-sm"><Plus size={14} className="mr-1.5" />Add</Button>
                </div>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-[#F9FAFB] border-b border-gray-200">
                  <tr>
                    <th className="text-left text-[10px] font-bold uppercase tracking-wider text-[#4B5563] py-2.5 px-4">Ingredient</th>
                    <th className="text-left text-[10px] font-bold uppercase tracking-wider text-[#4B5563] py-2.5 px-4">Status</th>
                    <th className="text-left text-[10px] font-bold uppercase tracking-wider text-[#4B5563] py-2.5 px-4">Tags</th>
                    <th className="text-left text-[10px] font-bold uppercase tracking-wider text-[#4B5563] py-2.5 px-4">Flags</th>
                    <th className="text-left text-[10px] font-bold uppercase tracking-wider text-[#4B5563] py-2.5 px-4">Version</th>
                    <th className="text-right text-[10px] font-bold uppercase tracking-wider text-[#4B5563] py-2.5 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(row => (
                    <IngredientRow key={row.id} row={row}
                      onEdit={(r) => { setEditing(r); setEditOpen(true); }}
                      onDelete={handleDelete}
                      onHistory={(r) => { setHistoryRow(r); setHistoryOpen(true); }} />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>

      <EditIngredientDialog open={editOpen} onClose={() => setEditOpen(false)} initial={editing} onSave={handleSave} />
      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)}
        onImported={(r) => { showToast(`Imported ${r.created} · Skipped ${r.skipped}`, 'success'); fetchList(); }} />
      <HistoryDialog open={historyOpen} onClose={() => setHistoryOpen(false)} row={historyRow} />

      {toast && (
        <div className={`fixed bottom-6 right-6 px-4 py-3 rounded shadow-lg text-sm font-semibold border ${
          toast.type === 'success' ? 'bg-[#DEF7EC] border-[#31C48D] text-[#03543F]' :
          toast.type === 'error' ? 'bg-[#FDE8E8] border-[#F8B4B4] text-[#9B1C1C]' :
          'bg-white border-gray-200 text-[#111827]'
        }`} data-testid="library-toast">
          {toast.type === 'success' && <CheckCircle size={14} weight="fill" className="inline mr-1 -mt-0.5" />}
          {toast.type === 'error' && <Warning size={14} weight="fill" className="inline mr-1 -mt-0.5" />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}
